import { isAffirmative, isNegative } from "../voice/ownerVoiceParsers";
import {
  OWNER_JARVIS_CONFIRMATION,
  isOwnerJarvisCapabilityContract,
  ownerJarvisOperationRequiresConfirmation,
} from "./ownerJarvisCapabilityContract";

const GENERIC_ERRORS = Object.freeze({
  execute: "No pude completar esa operación ahora. Probá de nuevo.",
  prepare: "No pude validar esa operación con suficiente seguridad. No hice cambios.",
  confirmation: "Necesito que confirmes esa operación antes de ejecutarla.",
  cancelled: "Listo, no hice ningún cambio.",
  unsupported: "Esa operación no está habilitada para esta capability.",
});

function messageFor(capability, operation, key) {
  return operation?.errors?.[key] || capability?.errors?.[key] || GENERIC_ERRORS[key] || GENERIC_ERRORS.execute;
}

function normalizeIntentMatch(match) {
  if (!match) return null;
  if (typeof match === "string") return { operationId: match, payload: undefined };
  const operationId = String(match.operationId || match.operation || "").trim();
  return operationId ? { ...match, operationId } : null;
}

function operationById(capability, operationId) {
  return capability.operations.find((operation) => operation.id === operationId) || null;
}

function publicDescriptor(capability) {
  if (!isOwnerJarvisCapabilityContract(capability)) {
    return {
      id: capability.id,
      domain: capability.domain || "legacy",
      contractVersion: 0,
      reads: [],
      operations: [{ id: "legacy-handle", kind: "legacy", confirmation: "legacy", invalidates: [], backendMethods: [] }],
    };
  }
  return {
    id: capability.id,
    domain: capability.domain,
    description: capability.description,
    contractVersion: capability.contractVersion,
    reads: [...capability.reads],
    operations: capability.operations.map((operation) => ({
      id: operation.id,
      kind: operation.kind,
      confirmation: operation.confirmation,
      requiresConfirmation: ownerJarvisOperationRequiresConfirmation(operation),
      voiceKey: operation.voice?.key || null,
      invalidates: [...(operation.invalidates || [])],
      backendMethods: [...(operation.backendMethods || [])],
    })),
  };
}

export function createOwnerJarvisCapabilityRegistry(capabilities = [], { snapshotService } = {}) {
  const ids = new Set();
  const voiceKeys = new Set();
  const registered = capabilities.filter(Boolean).map((capability) => {
    const isContract = isOwnerJarvisCapabilityContract(capability);
    if (!capability.id || (!isContract && typeof capability.handle !== "function")) {
      throw new Error("Cada capability de Jarvis debe declarar id y un contrato u handle() legacy.");
    }
    if (ids.has(capability.id)) throw new Error(`Capability de Jarvis duplicada: ${capability.id}`);
    ids.add(capability.id);
    if (isContract) {
      capability.operations.forEach((operation) => {
        if (!operation.voice) return;
        if (voiceKeys.has(operation.voice.key)) throw new Error(`Voice action de Jarvis duplicada: ${operation.voice.key}`);
        voiceKeys.add(operation.voice.key);
      });
    }
    return capability;
  });

  let pendingConfirmation = null;

  const prepareContractOperation = async (capability, operation, input, match = {}) => {
    if (typeof operation.prepare !== "function") return { ready: true, match };
    try {
      const prepared = await operation.prepare({ input, payload: match.payload, match, capability, operation });
      if (prepared?.ready === false) {
        return {
          ready: false,
          result: {
            handled: true,
            message: prepared.message || messageFor(capability, operation, "prepare"),
            ...(prepared.result || {}),
          },
        };
      }
      return {
        ready: true,
        match: {
          ...match,
          ...(prepared?.match || {}),
          payload: prepared?.payload !== undefined ? prepared.payload : match.payload,
          confirmationMessage: prepared?.confirmationMessage || match.confirmationMessage,
        },
      };
    } catch (error) {
      return {
        ready: false,
        result: {
          handled: true,
          message: messageFor(capability, operation, "prepare"),
          errorCode: error?.code || "CAPABILITY_PREPARE_FAILED",
        },
      };
    }
  };

  const executeContractOperation = async (capability, operation, input, match = {}, { confirmed = false, prepared = false } = {}) => {
    let effectiveMatch = match;
    if (!prepared) {
      const preparation = await prepareContractOperation(capability, operation, input, match);
      if (!preparation.ready) return preparation.result;
      effectiveMatch = preparation.match;
    }

    const requiresConfirmation = ownerJarvisOperationRequiresConfirmation(operation)
      || operation.confirmation === OWNER_JARVIS_CONFIRMATION.REQUIRED;
    if (requiresConfirmation && !confirmed) {
      pendingConfirmation = { capability, operation, input, match: effectiveMatch };
      return {
        handled: true,
        message: effectiveMatch.confirmationMessage || messageFor(capability, operation, "confirmation"),
        requiresConfirmation: true,
        operationKind: operation.kind,
      };
    }

    try {
      const result = await operation.execute({ input, payload: effectiveMatch.payload, match: effectiveMatch, capability, operation });
      if (result?.handled === false) return result;
      if (operation.invalidates?.length) snapshotService?.invalidate?.(operation.invalidates);
      return result?.handled ? result : { handled: true, ...(result || {}) };
    } catch (error) {
      return { handled: true, message: messageFor(capability, operation, "execute"), errorCode: error?.code || "CAPABILITY_EXECUTION_FAILED" };
    }
  };

  const dispatchPendingConfirmation = async (input) => {
    if (!pendingConfirmation) return null;
    if (isNegative(input?.raw || input?.text || "")) {
      const pending = pendingConfirmation;
      pendingConfirmation = null;
      if (typeof pending.operation.cancel === "function") {
        try {
          const result = await pending.operation.cancel({ input, payload: pending.match?.payload, match: pending.match, capability: pending.capability, operation: pending.operation });
          return { handled: true, cancelled: true, ...(result || {}), message: result?.message || messageFor(pending.capability, pending.operation, "cancelled") };
        } catch {
          return { handled: true, message: messageFor(pending.capability, pending.operation, "cancelled"), cancelled: true };
        }
      }
      return { handled: true, message: messageFor(pending.capability, pending.operation, "cancelled"), cancelled: true };
    }
    if (!isAffirmative(input?.raw || input?.text || "")) {
      return {
        handled: true,
        message: pendingConfirmation.match?.confirmationMessage || messageFor(pendingConfirmation.capability, pendingConfirmation.operation, "confirmation"),
        requiresConfirmation: true,
        operationKind: pendingConfirmation.operation.kind,
      };
    }
    const pending = pendingConfirmation;
    pendingConfirmation = null;
    return executeContractOperation(pending.capability, pending.operation, input, pending.match, { confirmed: true, prepared: true });
  };

  return {
    ids: registered.map((capability) => capability.id),
    describe() {
      return registered.map(publicDescriptor);
    },
    voiceActions() {
      const entries = [];
      registered.forEach((capability) => {
        if (!isOwnerJarvisCapabilityContract(capability)) return;
        capability.operations.forEach((operation) => {
          if (!operation.voice) return;
          const { key, ...voice } = operation.voice;
          entries.push([key, {
            ...voice,
            run: (context) => executeContractOperation(
              capability,
              operation,
              { context, inputSource: "voice-action" },
              { operationId: operation.id },
              { confirmed: operation.confirmation !== OWNER_JARVIS_CONFIRMATION.REQUIRED },
            ),
          }]);
        });
      });
      return Object.fromEntries(entries);
    },
    async execute({ capabilityId, operationId, payload, input = {}, confirmed = false } = {}) {
      const capability = registered.find((candidate) => candidate.id === capabilityId);
      if (!capability || !isOwnerJarvisCapabilityContract(capability)) {
        return { handled: true, message: GENERIC_ERRORS.unsupported };
      }
      const operation = operationById(capability, operationId);
      if (!operation) return { handled: true, message: messageFor(capability, null, "unsupported") };
      return executeContractOperation(capability, operation, input, { operationId, payload }, { confirmed });
    },
    async dispatch(input) {
      const pendingResult = await dispatchPendingConfirmation(input);
      if (pendingResult) return pendingResult;

      for (const capability of registered) {
        if (!isOwnerJarvisCapabilityContract(capability)) {
          const result = await capability.handle(input);
          if (result?.handled) return result;
          continue;
        }
        if (typeof capability.identify !== "function") continue;

        let match;
        try {
          match = normalizeIntentMatch(await capability.identify(input));
        } catch {
          return { handled: true, message: capability.errors.identify || capability.errors.execute || GENERIC_ERRORS.execute };
        }
        if (!match) continue;
        const operation = operationById(capability, match.operationId);
        if (!operation) return { handled: true, message: messageFor(capability, null, "unsupported") };
        return executeContractOperation(capability, operation, input, match);
      }
      return { handled: false };
    },
  };
}
